import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import Translate from '../../../../../../../../components/Translate';
import Field from '../../../../../../../../components-new/form/Field';
import Input from '../../../../../../../../components-new/form/Input';
import Switch from '../../../../../../../../components-new/form/Switch';
import ChoiceButtons from '../../../../../../../../components-new/form/ChoiceButtons';
import BlueButton from '../../../../../../../../components-new/buttons/BlueButton';
import DestructiveButton from '../../../../../../../../components-new/buttons/DestructiveButton';
import Tooltip from '../../../../../../../../components-new/general/Tooltip';
import DeleteIcon from '../../../../../../../../assets/icons/DeleteIcon';
import PlusIcon from '../../../../../../../../assets/icons/PlusIcon';
import GripVerticalIcon from '../../../../../../../../assets/icons/GripVerticalIcon';
import config from '../../../../../../../../config';
import {addNotification} from '../../../../../../../../services/notification/action';

const MAX_CUSTOM_INPUTS = 12;

const serializeInputs = inputs => inputs.map(({editorId, ...input}) => ({
    ...input, name: input.name.trim(), maxLength: Number(input.maxLength)
}));

const CustomInputs = ({inputs, disabled, onSave, addNotification}) => {
    const nextId = useRef(0);
    const withEditorIds = values => values.map(input => ({...input, editorId: nextId.current++}));
    const [draft, setDraft] = useState(() => withEditorIds(inputs || []));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);
    const [draggedId, setDraggedId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);

    useEffect(() => setDraft(withEditorIds(inputs || [])), [inputs]);

    const dirty = JSON.stringify(serializeInputs(draft)) !== JSON.stringify(serializeInputs(inputs || []));

    const update = (index, field, value) => {
        setDraft(previous => previous.map((input, i) => i === index ? {...input, [field]: value} : input));
        setError(false);
    };

    const reorder = (from, to) => {
        if (from < 0 || to < 0 || from === to || to >= draft.length) return;
        setDraft(previous => {
            const reordered = [...previous];
            const [moved] = reordered.splice(from, 1);
            reordered.splice(to, 0, moved);
            return reordered;
        });
        setError(false);
    };

    const save = async () => {
        if (draft.length > MAX_CUSTOM_INPUTS || draft.some(input => !input.name.trim() || !Number.isInteger(Number(input.maxLength)) || Number(input.maxLength) < 1)) {
            setError(true);
            addNotification('customInputsSaveError', config.notificationLevels.error);
            return;
        }
        setSaving(true);
        setError(false);
        try {
            await onSave(serializeInputs(draft));
            addNotification('saved', config.notificationLevels.success);
        } catch (error) {
            setError(true);
            addNotification('customInputsSaveError', config.notificationLevels.error);
        } finally {
            setSaving(false);
        }
    };

    const off = disabled || saving;
    return (
        <div className="wc-custom-inputs stg-grid__wide">
            <h3><Translate text="customInputs"/></h3>
            <p className="stg-card__note"><Translate text="customInputsHelp"/></p>
            {draft.map((input, index) => (
                <div className={'wc-info-input-card' + (dropTargetId === input.editorId ? ' wc-info-input-card--drop-target' : '')}
                     key={input.editorId}
                     onDragOver={event => {
                         if (off || draggedId === null) return;
                         event.preventDefault();
                         event.dataTransfer.dropEffect = 'move';
                         setDropTargetId(input.editorId);
                     }}
                     onDrop={event => {
                         if (off || draggedId === null) return;
                         event.preventDefault();
                         reorder(draft.findIndex(field => field.editorId === draggedId), index);
                         setDraggedId(null);
                         setDropTargetId(null);
                     }}>
                    <div className="wc-info-input-card__head">
                        <div className="wc-info-input-card__title">
                            <Tooltip text="customInputReorder">
                                <button type="button" className="wc-custom-inputs__drag-handle"
                                        disabled={off || draft.length < 2} draggable={!off && draft.length > 1}
                                        onDragStart={event => {
                                            setDraggedId(input.editorId);
                                            event.dataTransfer.effectAllowed = 'move';
                                            event.dataTransfer.setData('text/plain', String(input.editorId));
                                            event.dataTransfer.setDragImage(event.currentTarget.closest('.wc-info-input-card'), 20, 20);
                                        }}
                                        onDragEnd={() => { setDraggedId(null); setDropTargetId(null); }}
                                        onKeyDown={event => {
                                            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                                            event.preventDefault();
                                            reorder(index, index + (event.key === 'ArrowUp' ? -1 : 1));
                                        }}>
                                    <GripVerticalIcon width={18} height={18} stroke="currentColor"/>
                                    <span className="wc-custom-inputs__drag-label"><Translate text="customInputReorder"/></span>
                                </button>
                            </Tooltip>
                            <h3><Translate text="customInput"/> {index + 1}</h3>
                        </div>
                        <div className="wc-info-input-card__actions">
                            <Switch checked={input.enabled} disabled={off}
                                    onChange={value => update(index, 'enabled', value)}/>
                            <Tooltip text="remove">
                                <DestructiveButton type="button" variant="outline" size="sm" iconOnly
                                                   width="32px" disabled={off}
                                                   onClick={() => setDraft(previous => previous.filter(field => field.editorId !== input.editorId))}>
                                    <DeleteIcon width={18} height={18} stroke="currentColor"/>
                                </DestructiveButton>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="stg-grid">
                        {['name', 'label', 'placeholder'].map(field => (
                            <Field key={field} label={'customInput' + field.charAt(0).toUpperCase() + field.slice(1)}
                                   required={field === 'name'} htmlFor={'custom-input-' + index + '-' + field}>
                                <Input id={'custom-input-' + index + '-' + field} value={input[field] || ''}
                                       disabled={off} onChange={event => update(index, field, event.target.value)}/>
                            </Field>
                        ))}
                        <Field label="customInputMaxLength" required htmlFor={'custom-input-length-' + index}>
                            <Input id={'custom-input-length-' + index} type="number" min={1}
                                   value={input.maxLength} disabled={off}
                                   onChange={event => update(index, 'maxLength', event.target.value)}/>
                        </Field>
                        <Field label="customInputType">
                            <ChoiceButtons value={input.type} disabled={off}
                                           onChange={value => update(index, 'type', value)}
                                           options={['text', 'email', 'number'].map(type => ({
                                               value: type, label: <Translate text={'customInputType' + type}/>
                                           }))}/>
                        </Field>
                        <Field label="customInputRequired">
                            <Switch checked={input.isRequired} disabled={off}
                                    onChange={value => update(index, 'isRequired', value)}/>
                        </Field>
                    </div>
                </div>
            ))}
            {error && <p className="wc-custom-inputs__error"><Translate text="customInputsSaveError"/></p>}
            <div className="stg-control-row" style={{justifyContent:'space-between'}}>
                <BlueButton type="button" variant="soft" size="sm" width="auto" disabled={off || draft.length >= MAX_CUSTOM_INPUTS}
                            styles={{display: 'flex', alignItems: 'center', gap: '6px'}}
                              onClick={() => setDraft(previous => previous.length >= MAX_CUSTOM_INPUTS ? previous : [...previous, {
                                  editorId: nextId.current++,
                                  name: '', label: '', placeholder: '', isRequired: false,
                                  enabled: true, type: 'text', maxLength: 50
                              }])}>
                    <PlusIcon width={18} height={18} stroke="currentColor"/>
                    <Translate text="addCustomInput"/>
                </BlueButton>
                <BlueButton type="button" size="sm" width="180px" disabled={off || !dirty} loading={saving} onClick={save}>
                    <Translate text="save"/>
                </BlueButton>
            </div>
        </div>
    );
};

export default connect(null, {addNotification})(CustomInputs);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/webChatDesignAndConfig/components/CustomInputs.jsx